import { Box, Flex, Text, SimpleGrid, Spinner, Center } from '@chakra-ui/react';

import { FindShowcaseJobsQuery, OrderEnum } from 'lib/generated/graphql';
import { orderOptions } from 'lib/constants/order';
import { Pagination } from 'components/molecules/jobs/pagination';
import { ShowcaseCard } from 'components/molecules/jobs/showcaseCard';
import { UnauthenticatedShowInterestButton } from 'components/molecules/showInterestButton/unauthenticated';
import {
  useJobFilters,
  JobFiltersType,
} from 'lib/repositories/jobsShowcaseFilters';
import Heading from 'components/atoms/heading';
import Select from 'components/molecules/form/select';
import { EmptyList } from 'components/molecules/jobs/emptyList';

export type ShowcaseJobsListProps = {
  handleSearchJobs: (updatedFilters?: JobFiltersType) => void;
  jobs?: FindShowcaseJobsQuery['findShowcaseJobs']['data'];
  loading: boolean;
  total: number;
  isCandidateAuthenticated: boolean;
};

export const ShowcaseJobsList = ({
  handleSearchJobs,
  jobs,
  loading,
  total = 0,
  isCandidateAuthenticated,
}: ShowcaseJobsListProps) => {
  const { filters, setOrder } = useJobFilters();

  const handleOrderOnChange = (order: OrderEnum) => {
    setOrder(order);
    handleSearchJobs({ ...filters, order });
  };

  return (
    <Box as='section'>
      {jobs && jobs.length > 0 ? (
        <Flex as='header' justify='space-between'>
          <Text fontSize={16} fontWeight={400}>
            <Text as='span' color='primary.500' fontWeight={700} pr={1}>
              {total} vagas
            </Text>
            disponíveis
          </Text>

          <Box display={{ base: 'none', md: 'block' }}>
            <Select
              id='order'
              name='order'
              isSearchable={false}
              options={orderOptions}
              value={filters.order}
              placeholder='Selecione uma opção'
              onChange={(o) => handleOrderOnChange(o.target.value as OrderEnum)}
              variant='outline'
              selectedOptionStyle='check'
              chakraStyles={{
                container: (provided) => ({
                  ...provided,
                  w: 40,
                }),
                control: (provided) => ({
                  ...provided,
                  bgColor: 'primary.150',
                  borderColor: 'primary.500',
                  borderRadius: 20,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'primary.500',
                  _hover: {
                    borderColor: 'primary.500',
                  },
                  _focus: {
                    borderColor: 'primary.500',
                  },
                }),
                option: (provided, state) => ({
                  ...provided,
                  bgColor: 'white',
                  color: state.isSelected ? 'primary.500' : 'text.900',
                  fontSize: 14,
                  fontWeight: state.isSelected ? 700 : 400,
                  _hover: {
                    bgColor: 'primary.150',
                  },
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: 'primary.500',
                }),
                dropdownIndicator: (provided) => ({
                  ...provided,
                  padding: 2,
                  bgColor: 'transparent',
                  color: 'primary.500',
                }),
              }}
            />
          </Box>
        </Flex>
      ) : null}

      <SimpleGrid columns={1} spacing={6} pt={5}>
        {loading ? (
          <Center p={8}>
            <Spinner color='primary.500' />
          </Center>
        ) : (
          <>
            {jobs && jobs?.length > 0 ? (
              jobs?.map((job, index) => (
                <div key={job.id}>
                  <ShowcaseCard
                    jobId={job.id}
                    key={job.id}
                    focus={job.focus?.description}
                    acceptRemote={job.remoteWork}
                    city={job.city?.name}
                    slug={job.slug}
                    techs={job.technologies ?? []}
                    title={job.title}
                    cltSalaryMin={job.cltMinSalary}
                    cltSalaryMax={job.cltMaxSalary}
                    pjSalaryMin={job.pjMinSalary}
                    pjSalaryMax={job.pjMaxSalary}
                    usdAnnualSalaryMin={job.usdAnnualSalaryMin}
                    usdAnnualSalaryMax={job.usdAnnualSalaryMax}
                    createdAt={job.createdAt}
                    experienceLevel={job.experienceLevel}
                    requirements={job.requirements}
                    description={job.description}
                    hideSalary={job.hideSalary}
                    isJobInternational={job.isInternational}
                    hybrid={job.hybrid}
                    jobType={job.jobType}
                    companySlug={job.company.slug}
                  />

                  {!isCandidateAuthenticated && index === 2 && (
                    <Flex
                      alignItems='center'
                      bg='primary.150'
                      flexDir={{ base: 'column', lg: 'row' }}
                      p={8}
                    >
                      <Heading
                        color='primary.500'
                        fontWeight={700}
                        format='h3'
                        m={0}
                        p={0}
                        fontSize='2xl'
                        letterSpacing={0}
                        textAlign={['center', 'center', 'center', 'left']}
                      >
                        Crie seu perfil e receba diversas propostas de emprego!
                        🚀
                      </Heading>

                      <UnauthenticatedShowInterestButton
                        ml={{ base: 0, lg: 4 }}
                        mt={{ base: 4, lg: 0 }}
                        size='xl'
                        w={{ base: 'full', lg: 'auto' }}
                        whiteSpace={{ base: 'break-spaces', lg: 'nowrap' }}
                      />
                    </Flex>
                  )}
                </div>
              ))
            ) : (
              <EmptyList isCandidateAuthenticated={isCandidateAuthenticated} />
            )}

            <Pagination total={total} handleSearchJobs={handleSearchJobs} />
          </>
        )}
      </SimpleGrid>
    </Box>
  );
};
